<div class="profile">
  <h1>{{ 'shell.profile.title' | translate }}</h1>

  <!-- Language — full-row dropdown (mockup Option A): the whole row is the
       trigger, and the label comes from the i18n file (miscUi.lang*), not a
       hardcoded "English", so the active variant reads as US / UK / Hebrew.
       India (market=IN) shows a SINGLE neutral English option — Hebrew + UK are
       hidden so the locale can never leave en-IN.json (DMRA prohibition §3).
       Latin labels are wrapped dir="ltr" so the parentheses render correctly
       inside the RTL screen. Selecting a language never touches market/currency. -->
  <section class="card lang-card">
    <button type="button" class="lang-trigger" [matMenuTriggerFor]="profileLangMenu"
            aria-haspopup="listbox">
      <span class="card-title lang-trigger-label">{{ 'shell.profile.language' | translate }}</span>
      <span class="lang-current">
        <svg class="lang-globe" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
        <span class="lang-current-label" [attr.dir]="pickerLang === 'he' ? 'rtl' : 'ltr'">{{ currentLangLabelKey | translate }}</span>
        <svg class="lang-chev" viewBox="0 0 24 24" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>
      </span>
    </button>
    <mat-menu #profileLangMenu="matMenu" class="profile-lang-menu">
      <ng-container *ngIf="isIndiaMarket; else fullProfilePicker">
        <button mat-menu-item type="button" (click)="switchLang('en')" [class.active]="true">
          <span dir="ltr">{{ 'miscUi.langEnglishOnly' | translate }}</span>
        </button>
      </ng-container>
      <ng-template #fullProfilePicker>
        <button mat-menu-item type="button" (click)="switchLang('en')" [class.active]="pickerLang === 'en'">
          <span dir="ltr">{{ 'miscUi.langEnglish' | translate }}</span>
        </button>
        <button mat-menu-item type="button" (click)="switchLang('en-GB')" [class.active]="pickerLang === 'en-GB'">
          <span dir="ltr">{{ 'miscUi.langEnglishUk' | translate }}</span>
        </button>
        <button mat-menu-item type="button" (click)="switchLang('he')" [class.active]="pickerLang === 'he'">
          <span>{{ 'miscUi.langHebrew' | translate }}</span>
        </button>
      </ng-template>
    </mat-menu>
  </section>

  <!-- Identity -->
  <section class="card">
    <div class="card-title">{{ 'shell.profile.identity' | translate }}</div>
    <label class="fld-label">{{ 'shell.profile.email' | translate }}</label>
    <input class="fld readonly" type="email" [value]="user.email" readonly disabled />
    <div class="fld-help">{{ 'shell.profile.emailHelp' | translate }}</div>
    <label class="fld-label">{{ 'shell.profile.fname' | translate }}</label>
    <input class="fld" type="text" [(ngModel)]="user.fname" (change)="saveIdentity()" />
    <label class="fld-label">{{ 'shell.profile.lname' | translate }}</label>
    <input class="fld" type="text" [(ngModel)]="user.lname" (change)="saveIdentity()" />
    <label class="fld-label">{{ 'shell.profile.idNumber' | translate }}</label>
    <input class="fld" type="text" inputmode="numeric" [(ngModel)]="user.idNumber" (change)="saveIdentity()"
           [attr.placeholder]="'shell.profile.idNumberPlaceholder' | translate" />
    <div class="fld-help">{{ 'shell.profile.idNumberHelp' | translate }}</div>
  </section>

  <!-- Subscription -->
  <section class="card sub">
    <!-- Paid: real subscription / credits state -->
    <ng-container *ngIf="isEntitled">
      <div class="card-title brand">{{ (isMaccabi ? 'shell.profile.maccabiPlan' : 'shell.profile.subscription') | translate }}</div>
      <div class="sub-row">
        <span class="dot-green"></span>
        <span>{{ 'shell.profile.status' | translate }}: {{ 'shell.profile.active' | translate }}</span>
      </div>
      <div class="sub-remaining">{{ 'shell.profile.remaining' | translate }}: {{ hasPaidTerm ? ('shell.profile.unlimitedTests' | translate) : remainingTests }}</div>
      <button class="btn-pk" (click)="startNewAnalysis()">{{ 'shell.profile.newTest' | translate }}</button>
      <button
        *ngIf="storeBilling.enabled && (sessionService.session?.user?.paidPlan === 'monthly' || sessionService.session?.user?.paidPlan === 'annual')"
        class="btn-outline-pk"
        (click)="storeBilling.manageSubscriptions()"
      >{{ 'payment.store.manage' | translate }}</button>
    </ng-container>
    <!-- Restore purchases: reachable for every native user, paid or not (Apple 3.1.1) -->
    <ng-container *ngIf="storeBilling.enabled">
      <button class="btn-outline-pk" (click)="restorePurchases()" [disabled]="restoring">
        {{ (restoring ? 'payment.store.restoring' : 'payment.store.restore') | translate }}
      </button>
      <p class="sub-remaining" *ngIf="restoreMessage" role="status">{{ restoreMessage }}</p>
    </ng-container>
    <!-- Unpaid: free-plan state (locked-flow spec §118) -->
    <ng-container *ngIf="!isEntitled">
      <div class="card-title brand">{{ 'profile.locked.planTitle' | translate }}</div>
      <div class="sub-row">
        <span class="plan-free">{{ 'profile.locked.planFree' | translate }}</span>
      </div>
      <!-- Has a saved first scan: view it (free) + upsell the full picture (paywall) -->
      <ng-container *ngIf="firstScanGuid">
        <div class="sub-remaining">{{ 'profile.locked.planSub' | translate }}</div>
        <button class="btn-outline-pk" (click)="viewFirstResults()">{{ 'profile.locked.viewFirstReview' | translate }}</button>
        <button class="btn-pk" (click)="openPaywall()">{{ 'profile.locked.planCta' | translate }}</button>
      </ng-container>
      <!-- No completed scan yet: invite the FREE first scan, not the paywall (there
           is no "full picture" to open with zero data). N718 -->
      <ng-container *ngIf="!firstScanGuid">
        <div class="sub-remaining">{{ 'profile.locked.planSubNoScan' | translate }}</div>
        <button class="btn-pk" (click)="startNewAnalysis()">{{ 'profile.locked.planCtaNoScan' | translate }}</button>
      </ng-container>
    </ng-container>
  </section>

  <!-- Download the app (app-download spec §2). Deliberately here, between the
       subscription and the monthly card: below this sit the two long lists
       (history + pending scans), and anything under them is never seen. The
       component hides itself inside the app and on Peritale. -->
  <section class="app-download-row" [class.card]="!appDownloadIsCard" *ngIf="showAppDownload">
    <app-download-card variant="row" placement="profile"></app-download-card>
  </section>

  <!-- Monthly stats -->
  <section class="card">
    <div class="card-title">{{ 'shell.profile.monthly' | translate }}</div>
    <div class="stats">
      <div class="stat"><div class="stat-num">{{ isEntitled ? activeDays : 0 }}</div><div class="stat-lbl">{{ 'shell.profile.activeDays' | translate }}</div></div>
      <div class="stat"><div class="stat-num">{{ isEntitled ? streak : 0 }}</div><div class="stat-lbl">{{ 'shell.profile.streak' | translate }}</div></div>
      <div class="stat"><div class="stat-num">{{ isEntitled ? completionsThisMonth : 0 }}</div><div class="stat-lbl">{{ 'shell.profile.completions' | translate }}</div></div>
    </div>
    <!-- Unpaid: numbers forced to 0 + teaser (locked-flow spec §116, §141) -->
    <div class="monthly-teaser" *ngIf="!isEntitled">
      <p class="muted small">{{ monthlyTeaserKey | translate }}</p>
      <a class="teaser-link" (click)="openPaywall()">{{ 'profile.locked.teaserLink' | translate }}</a>
    </div>
  </section>

  <!-- Test history -->
  <section class="card">
    <div class="card-title">{{ 'shell.profile.history' | translate }}</div>
    <div *ngIf="isScansLoading" class="muted">...</div>
    <div *ngIf="!isScansLoading && visibleScans.length === 0" class="muted">
      {{ 'shell.profile.noHistory' | translate }}
    </div>
    <!-- Unpaid: only the first (free) scan is shown (locked-flow spec §119) -->
    <!-- What she finished, newest first. The link says where it goes instead of
         repeating the status word beside it. -->
    <div class="scan-row" *ngFor="let s of completedScans">
      <span class="dot green"></span>
      <span class="scan-main">
        {{ 'userAccount.statusCompleted' | translate }}
        <span class="scan-date" *ngIf="s.created">· {{ s.created | date: 'dd.MM.yyyy' }}</span>
      </span>
      <span class="scan-links">
        <a (click)="navigateToResults(s.guid)">{{ 'shell.profile.viewResults' | translate }}</a>
      </span>
    </div>
    <!-- What she started and did not finish: one line that opens, rather than a
         dozen identical Pending rows from the same day. -->
    <details class="scan-unfinished" *ngIf="unfinishedScans.length">
      <summary>
        <span class="dot amber"></span>
        <span class="scan-main">{{ (unfinishedScans.length === 1 ? 'shell.profile.unfinishedOne' : 'shell.profile.unfinished') | translate: { n: unfinishedScans.length } }}</span>
      </summary>
      <div class="scan-row" *ngFor="let s of unfinishedShown">
        <span class="dot amber"></span>
        <span class="scan-main">
          {{ 'userAccount.statusPending' | translate }}
          <span class="scan-date" *ngIf="s.created">· {{ s.created | date: 'dd.MM.yyyy' }}</span>
        </span>
        <span class="scan-links">
          <a (click)="navigateToTest(s.guid)">{{ 'shell.profile.continueScan' | translate }}</a>
        </span>
      </div>
      <p class="muted small unfinished-more" *ngIf="unfinishedHidden">
        {{ 'shell.profile.unfinishedMore' | translate: { n: unfinishedHidden } }}
      </p>
    </details>
  </section>

  <!-- Pending-locked scans: "בדיקות שממתינות לפתיחה" (pending-locked spec).
       Only completed scans the account hasn't unlocked yet; empty for entitled
       accounts (section hides). Distinct from the free first scan above. -->
  <section class="card pending-locked" *ngIf="lockedScans.length">
    <div class="card-title">{{ 'profile.pendingScans.title' | translate }}</div>
    <div class="muted pending-sub">{{ 'profile.pendingScans.subtitle' | translate }}</div>
    <div class="pending-item" *ngFor="let s of lockedScans">
      <div class="pending-date" *ngIf="s.created">{{ s.created | date: 'dd.MM.yyyy' }}</div>
      <div class="pending-hook">{{ 'profile.pendingScans.valueHook' | translate }}</div>
      <div class="pending-compare">{{ 'profile.pendingScans.compare' | translate }}</div>
      <button type="button" class="btn-pk pending-cta" (click)="navigateToResults(s.guid)">
        {{ 'profile.pendingScans.cta' | translate }}
      </button>
    </div>
  </section>

  <!-- My doctors (Maccabi) -->
  <section class="card" *ngIf="doctors.length">
    <div class="card-title">{{ 'shell.profile.doctors' | translate }}</div>
    <div class="scan-row" *ngFor="let d of doctors">
      <span class="scan-main">{{ d.name || d.email }}</span>
      <span class="scan-links"><a (click)="removeDoctor(d.linkId)">✕</a></span>
    </div>
  </section>

  <!-- Contraception toggle (custom switch — native checkbox-in-label double-toggled on iOS) -->
  <section class="card">
    <div class="toggle-row">
      <span class="toggle-label">{{ 'shell.profile.contraceptionToggle' | translate }}</span>
      <span class="switch" [class.on]="contraceptionOn" role="switch" [attr.aria-checked]="contraceptionOn"
            tabindex="0" (click)="toggleContraception(!contraceptionOn)"
            (keydown.enter)="toggleContraception(!contraceptionOn)" (keydown.space)="toggleContraception(!contraceptionOn)">
        <span class="switch-knob"></span>
      </span>
    </div>
    <div class="toggle-state muted small">
      {{ (contraceptionOn ? 'shell.profile.contraceptionStateOn' : 'shell.profile.contraceptionStateOff') | translate }}
    </div>
  </section>

  <!-- Notification / routine settings -->
  <section class="card" [class.locked-card]="notifLocked">
    <div class="card-title">{{ 'shell.profile.notifications' | translate }}</div>
    <div class="muted small">{{ 'shell.profile.notificationsBody' | translate }}</div>

    <!-- Locked in Option A (unpaid + freeTierToolsLocked): notifications ship
         with the tools, so they lock together. Tap -> paywall (spec §117, §158). -->
    <div class="notif-locked" *ngIf="notifLocked" (click)="openPaywall()" role="button" tabindex="0">
      <span class="notif-lock-ico" aria-hidden="true">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>
      </span>
      <span class="notif-lock-cta">{{ 'profile.locked.planCta' | translate }}</span>
    </div>

    <!-- Open (Option B, or paid): routine rows -->
    <ng-container *ngIf="!notifLocked">
      <div class="routine-row" *ngFor="let tool of tools">
        <label class="routine-name">
          <input type="checkbox" [checked]="isToolActive(tool.id)" (change)="toggleToolActive(tool.id)" />
          {{ toolName(tool.id) }}
        </label>
        <div class="routine-cfg" *ngIf="isToolActive(tool.id)">
          <span class="chip" *ngFor="let f of freqOpts" [class.on]="schedule[tool.id]?.freq === f" (click)="setFreq(tool.id, f)">
            {{ 'shell.profile.freq.' + f | translate }}
          </span>
        </div>
        <div class="routine-cfg" *ngIf="isToolActive(tool.id)">
          <span class="chip dark" *ngFor="let tm of timeOpts" [class.on]="schedule[tool.id]?.time === tm" (click)="setTime(tool.id, tm)">
            {{ 'shell.profile.time.' + tm | translate }}
          </span>
        </div>
        <!-- B13: what she actually gets, in plain language. -->
        <div class="routine-next" *ngIf="nextReminderText(tool.id)">{{ nextReminderText(tool.id) }}</div>
      </div>
    </ng-container>
  </section>

  <!-- Home-screen widget -->
  <section class="card widget-link" (click)="navigateToWidgetSettings()">
    <div>
      <div class="card-title">{{ 'widget.settings.title' | translate }}</div>
      <div class="muted small">{{ 'widget.settings.subtitle' | translate }}</div>
    </div>
    <span class="widget-arrow">›</span>
  </section>

  <!-- Privacy & consents (V-12 / V-13 / V-16) -->
  <section class="card" id="privacy">
    <div class="card-title">{{ 'privacySettings.title' | translate }}</div>
    <div class="muted small">{{ 'shell.profile.privacyBody' | translate }}</div>
    <app-privacy-settings (requestDeletion)="requestAccountDeletion()"></app-privacy-settings>
  </section>

  <!-- Test notification — staging/dev only -->
  <section class="card" *ngIf="showTestNotif">
    <div class="card-title">{{ 'shell.profile.testNotif.title' | translate }}</div>
    <div class="muted small">{{ 'shell.profile.testNotif.body' | translate }}</div>
    <button class="btn-pk" [disabled]="testingNotif || notifBlocked" (click)="sendTestNotification()">
      {{ (testingNotif ? 'shell.profile.testNotif.sending' : 'shell.profile.testNotif.send') | translate }}
    </button>
    <div class="muted small" *ngIf="notifStatus">{{ notifStatus }}</div>
  </section>

  <!-- Admin (admins only) — moved here from the removed hamburger header -->
  <section class="card admin-link" *ngIf="isAdmin" (click)="navigateToAdmin()">
    {{ 'shell.profile.admin' | translate }}
  </section>

  <!-- Log out -->
  <section class="card logout-row" (click)="logout()">
    {{ 'shell.profile.logout' | translate }}
  </section>

  <!-- Delete -->
  <section class="card delete-pending" *ngIf="deletionScheduledFor">
    <div>{{ 'shell.profile.deleteScheduled' | translate: { date: (deletionScheduledFor | date: 'mediumDate') } }}</div>
    <button type="button" class="btn-link" [disabled]="deletionBusy" (click)="cancelAccountDeletion()">
      {{ (deletionBusy ? 'shell.profile.deleteBusy' : 'shell.profile.deleteCancel') | translate }}
    </button>
  </section>
  <button type="button" class="card delete" *ngIf="!deletionScheduledFor" [disabled]="deletionBusy" (click)="requestAccountDeletion()">
    {{ (deletionBusy ? 'shell.profile.deleteBusy' : 'shell.profile.delete') | translate }}
  </button>
  <div class="muted small" *ngIf="deletionError">{{ deletionError }}</div>
</div>
